import type {
  UiOptions,
  ValidationContext,
  ValidationOptions,
} from './sanitizedTextField.types';

/**
 * Default UI options for SanitizedTextField component
 */
export const defaultUiOptions: UiOptions = {
  autofocus: false,
  placeholder: undefined,
  variant: 'standard',
  minLength: 3,
  maxLength: 63,
  allowSpecialChars: false,
  disableUppercase: false,
  disableSpaces: false,
  transformToKebabCase: false,
  required: false,
  customRegex: undefined,
};

/**
 * Build a validation regex pattern based on text field options
 */
export function getValidationRegex(options: {
  disableUppercase: boolean;
  disableSpaces: boolean;
  allowSpecialChars: boolean;
}): RegExp {
  const { disableUppercase, disableSpaces, allowSpecialChars } = options;

  const patterns = {
    letters: disableUppercase ? 'a-z' : 'a-zA-Z',
    numbers: '0-9',
    spaces: disableSpaces ? '' : ' ',
    specialChars: allowSpecialChars ? '-_@./#&+' : '-',
  };

  const characterSet = [
    patterns.letters,
    patterns.numbers,
    patterns.spaces,
    patterns.specialChars,
  ].join('');

  return new RegExp(`^[${characterSet}]+$`);
}

/**
 * Get all validation error messages based on field rules
 * Returns an empty array if the value is valid
 */
export function getAllValidationErrors(
  value: string,
  options: ValidationOptions,
): string[] {
  const {
    required,
    minLength,
    maxLength,
    disableUppercase,
    disableSpaces,
    allowSpecialChars,
    transformToKebabCase,
    customRegex,
  } = options;

  const errors: string[] = [];

  if (!value && required) {
    errors.push('This field is required');
    return errors; // Skip other validations for empty required fields
  }

  // Skip other validations for empty non-required values
  if (!value) return errors;

  // Check for inputs consisting only of special characters
  if (value.trim() && /^[-_@./#&+]+$/.test(value)) {
    errors.push('Input cannot consist only of special characters');
  }

  // Check for inputs starting or ending with special characters
  if (value.trim() && /^[-_@./#&+]|[-_@./#&+]$/.test(value)) {
    errors.push('Input cannot start or end with special characters');
  }
  if (value.length < minLength) {
    errors.push(`Must be at least ${minLength} characters`);
  }

  if (value.length > maxLength) {
    errors.push(`Must not exceed ${maxLength} characters`);
  }

  if (
    !getValidationRegex({
      disableUppercase,
      disableSpaces,
      allowSpecialChars,
    }).test(value)
  ) {
    const parts = [
      disableUppercase ? 'lowercase letters' : 'letters',
      'numbers',
      // Omit spaces from the message if they'll be transformed to kebab case
      !disableSpaces && !transformToKebabCase && 'spaces',
      allowSpecialChars ? 'special characters' : 'hyphens',
    ].filter(Boolean);

    errors.push(
      `Only ${parts.slice(0, -1).join(', ')} and ${
        parts[parts.length - 1]
      } allowed`,
    );
  }

  if (customRegex && value) {
    try {
      const regex = new RegExp(customRegex);
      if (!regex.test(value)) {
        errors.push(
          `Input does not match the required pattern: /${customRegex}/`,
        );
      }
    } catch (error) {
      errors.push('Invalid custom regex pattern');
      // eslint-disable-next-line no-console -- Log the error for debugging. Useful for users as well
      console.error(
        'Invalid custom regex pattern provided for validation:',
        customRegex,
        error,
      );
    }
  }

  return errors;
}

/**
 * Transform input value according to configuration
 *
 * @param value The input string to transform
 * @param transformToKebabCase Whether to transform spaces to hyphens
 * @returns The transformed string
 */
export function transformValue(
  value: string,
  transformToKebabCase: boolean,
): string {
  if (transformToKebabCase) {
    return value.replace(/\s+/g, '-');
  }

  return value;
}

/**
 * Merges user-provided UI options with defaults
 *
 * @param uiSchema The UI schema containing user-defined options
 * @returns Merged UI options
 */
export function mergeWithDefaultUiOptions(uiSchema?: {
  'ui:options'?: Record<string, any>;
}): UiOptions {
  return {
    ...defaultUiOptions,
    ...(uiSchema?.['ui:options'] || {}),
  };
}

/**
 * Extracts validation options from the validation context
 *
 * @param context The validation context object
 * @returns Validation options
 */
export function getValidationOptions(
  context: ValidationContext,
): ValidationOptions {
  const uiOptions = mergeWithDefaultUiOptions(context.uiSchema);

  return {
    required: context.schema.required === true,
    minLength: uiOptions.minLength,
    maxLength: uiOptions.maxLength,
    allowSpecialChars: uiOptions.allowSpecialChars,
    disableUppercase: uiOptions.disableUppercase,
    disableSpaces: uiOptions.disableSpaces,
    transformToKebabCase: uiOptions.transformToKebabCase,
    customRegex: uiOptions.customRegex,
  };
}
